import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  BookOpen,
  ChefHat,
  Settings,
  CalendarDays,
  Compass,
  Refrigerator,
  Heart,
  Clock,
  Lightbulb,
} from 'lucide-react';
import { useCookingStore } from '@/stores/cooking-store';
import { useCookbookStore } from '@/stores/cookbook-store';
import { useAuthStore } from '@/stores/auth-store';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  end?: boolean;
}

function isRouteActive(pathname: string, targetPath: string, end = false): boolean {
  if (end) return pathname === targetPath;
  if (targetPath === '/') return pathname === '/';
  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
}

export function DesktopSidebar() {
  const location = useLocation();
  const { t } = useTranslation(['common', 'cooking', 'auth']);
  const isSessionActive = useCookingStore((state) => state.isSessionActive);
  const user = useAuthStore((state) => state.user);
  const favoritesCount = useCookbookStore((state) => state.getFavorites().length);
  const sidebarTips = t('cooking:sidebarTips', { returnObjects: true }) as string[];
  const [tip] = React.useState(() => sidebarTips[Math.floor(Math.random() * sidebarTips.length)]);

  if (location.pathname.startsWith('/cook/session')) {
    return null;
  }

  const primaryNavItems: NavItem[] = [
    { to: '/', label: t('common:nav.home'), icon: Home, end: true },
    { to: '/discover', label: t('common:nav.discover'), icon: Compass },
    { to: '/planner', label: t('common:nav.mealPlanner'), icon: CalendarDays },
    { to: '/pantry', label: t('common:nav.pantry'), icon: Refrigerator },
    { to: '/cookbook', label: t('common:nav.myCookbook'), icon: BookOpen },
    { to: '/cook', label: t('common:nav.letsCook'), icon: ChefHat },
  ];

  const secondaryNavItems: NavItem[] = [
    { to: '/cookbook?filter=favorites', label: t('common:nav.favorites'), icon: Heart, badge: favoritesCount > 0 ? favoritesCount : undefined },
    { to: '/cookbook?filter=recent', label: t('common:nav.recentlyCooked'), icon: Clock },
  ];

  const navItemStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    fontWeight: 500,
    fontSize: '14px',
    color: isActive ? 'var(--accent-primary-hover)' : 'var(--text-secondary)',
    background: isActive ? 'var(--accent-primary-bg)' : 'transparent',
    transform: isActive ? 'translateX(var(--sidebar-active-nudge, 2px))' : 'translateX(0)',
    textDecoration: 'none',
    transition: 'background var(--motion-duration-fast) var(--motion-ease-standard), color var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-standard) var(--motion-ease-emphasis)',
  });

  return (
    <>
    <aside
      style={{
        position: 'fixed',
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        zIndex: 30,
        width: '260px',
        background: 'white',
        borderInlineEnd: '1px solid var(--border-primary)',
        display: 'none', // Hidden by default (mobile)
        flexDirection: 'column',
        overflowY: 'auto',
        animation: 'fadeIn 260ms var(--motion-ease-emphasis)',
      }}
      className="desktop-sidebar"
      role="navigation"
      aria-label={t('common:a11y.sidebarNav')}
    >
      {/* Logo */}
      <div style={{ 
        height: '64px', 
        display: 'flex', 
        alignItems: 'center', 
        padding: '0 20px',
        borderBottom: '1px solid var(--border-secondary)'
      }}>
        <NavLink to="/" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
          <img
            src="/logo.png"
            alt={t('common:app.name')}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--radius-lg)',
              objectFit: 'contain',
            }}
          />
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)' }}>{t('common:app.name')}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{t('common:app.tagline')}</div>
          </div>
        </NavLink>
      </div>

      {/* User info */}
      {user && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: user.photoURL ? 'transparent' : 'linear-gradient(135deg, var(--accent-primary), var(--accent-primary-hover))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {user.photoURL ? (
                <img src={user.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
              ) : (
                <span style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>
                  {(user.displayName ?? user.email ?? '?')[0]?.toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.displayName ?? t('auth:guest')}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary Navigation */}
      <nav style={{ flex: 1, padding: '16px 12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {primaryNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = isRouteActive(location.pathname, item.to, item.end);
            const showIndicator = item.to === '/cook' && isSessionActive;
            
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                style={navItemStyle(isActive)}
              >
                <span style={{ position: 'relative' }}>
                  <Icon style={{ width: '20px', height: '20px' }} />
                  {showIndicator && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-2px',
                        insetInlineEnd: '-2px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'var(--accent-success)',
                        border: '2px solid white',
                        animation: 'pulse 1.8s ease-in-out infinite',
                      }}
                    />
                  )}
                </span>
                <span style={{ fontWeight: isActive ? 600 : 500 }}>{item.label}</span>
              </NavLink>
            );
          })}
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: 'var(--border-secondary)', margin: '16px 0' }} />

        {/* Secondary Navigation */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            padding: '0 14px', 
            fontSize: '10px', 
            fontWeight: 700, 
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '8px'
          }}>
            {t('common:nav.quickAccess')}
          </div>
          {secondaryNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.search.includes(item.to.split('?')[1] || '');
            
            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={{
                  ...navItemStyle(isActive),
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon style={{ width: '20px', height: '20px' }} />
                  {item.label}
                </span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span style={{
                    background: '#CFFAFE',
                    color: 'var(--accent-primary-hover)',
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: '100px',
                    minWidth: '20px',
                    textAlign: 'center',
                  }}>
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>

        {/* Pro Tip */}
        <div style={{ 
          margin: '24px 4px 0',
          padding: '16px',
          background: 'linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid #CFFAFE',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Lightbulb style={{ width: '16px', height: '16px', color: 'var(--accent-primary)' }} />
            <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--accent-primary-hover)', textTransform: 'uppercase' }}>
              {t('common:proTip')}
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
            {tip}
          </p>
        </div>
      </nav>

      {/* Settings */}
      <div style={{ padding: '12px', borderTop: '1px solid var(--border-secondary)' }}>
        <NavLink
          to="/settings"
          style={navItemStyle(location.pathname === '/settings')}
        >
          <Settings style={{ width: '20px', height: '20px' }} />
          {t('common:nav.settings')}
        </NavLink>
      </div>
    </aside>

    {/* Show sidebar on desktop (lg: 1024px and up) */}
    <style>{`
      @media (min-width: 1024px) {
        .desktop-sidebar {
          display: flex !important;
        }
      }
    `}</style>
    </>
  );
}
